import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Tooltip, IconButton, Typography, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AutoModeIcon from "@mui/icons-material/AutoMode";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppSelectFilter from "../components/common/AppSelectFilter";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import StudentSearchSelect from "../components/common/StudentSearchSelect";
import TermSelectFilter from "../components/common/TermSelectFilter";
import VoteHeadSelectFilter from "../components/common/VoteHeadSelectFilter";
import ClassroomSelectFilter from "../components/common/ClassroomSelectFilter";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import StatusChip from "../components/common/StatusChip";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";
import FeeReminderModal from "../components/studentFeeLedgers/FeeReminderModal";
import AppModal from "../components/common/AppModal";
import AppLinearProgress from "../components/common/AppLinearProgress";

import useDebounce from "../hooks/useDebounce";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { getTerms } from "../redux/actions/termActions";
import { getClassrooms } from "../redux/actions/classroomActions";
import { getVoteHeads } from "../redux/actions/voteHeadActions";
import {
  getLedgerEntries,
  generateTermFees,
  uploadBulkPayments,
  resetFinanceStateAction,
} from "../redux/actions/studentFeeLedgerActions";
import { usePermissions } from "../hooks/usePermissions";
import { StudentFeeLedgerResponseDto } from "../shared/dtos/studentFeeLedger/StudentFeeLedgerResponseDto";
import { TransactionType } from "../shared/enums/TransactionType";
import { PaymentMethod } from "../shared/enums/PaymentMethod";
import { TRANSACTION_TYPE_OPTIONS } from "../shared/data/enumOptions";
import { PAYMENT_METHOD_OPTIONS } from "../shared/data/enumOptions";
import { formatPlainNumber } from "../utils/appUtils";

export interface StudentFeeLedgerQueryParams {
  branchId?: string;
  studentId?: string;
  termId?: string;
  type?: TransactionType;
  paymentMethod?: PaymentMethod;
  voteHeadId?: string;
  search?: string;
  page?: number;
  size?: number;
}

const StudentFeeLedgersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const {
    ledgerEntries,
    pagination,
    loadingLedgerEntries,
    errorLoadingLedgerEntries,
    loadingBatchGeneration,
    loadingReminders,
    loadingUpload,
    errorReminders,
    serverStatus,
  } = useAppSelector((state) => state.studentFeeLedger);

  const { items: terms } = useAppSelector((state) => state.term);
  const { items: classrooms } = useAppSelector((state) => state.classroom);
  const { userInfo } = useAppSelector((state) => state.auth);

  // Filters State
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 400);

  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [selectedStudentId, setSelectedStudentId] = useState<string>("");
  const [selectedTerm, setSelectedTerm] = useState<string>("");
  const [selectedType, setSelectedType] = useState<string>("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<string>("");
  const [selectedVoteHeadId, setSelectedVoteHeadId] = useState<string>("");
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  // Modals
  const [selectedClassroom, setSelectedClassroom] = useState<string>("");
  const [isGenerateOpen, setIsGenerateOpen] = useState<boolean>(false);
  const [isReminderModalOpen, setIsReminderModalOpen] =
    useState<boolean>(false);
  const [isUploadOpen, setIsUploadOpen] = useState<boolean>(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const canCreate = hasPermission("fee_ledger:create");

  useEffect(() => {
    dispatch(getTerms());
    dispatch(getClassrooms());
    dispatch(getVoteHeads());
  }, [dispatch]);

  useEffect(() => {
    if (terms && terms.length > 0 && !selectedTerm) {
      const active = terms.find((t) => t.isCurrentTerm) || terms[0];
      if (active) setSelectedTerm(active.id);
    }
  }, [terms, selectedTerm]);

  const fetchEntries = useCallback((): void => {
    dispatch(
      getLedgerEntries({
        page: pageConfig.page,
        size: pageConfig.limit,
        branchId: selectedBranchId || undefined,
        studentId: selectedStudentId || undefined,
        termId: selectedTerm || undefined,
        type: (selectedType as TransactionType) || undefined,
        paymentMethod: (selectedPaymentMethod as PaymentMethod) || undefined,
        voteHeadId: selectedVoteHeadId || undefined,
        search: debouncedSearch.trim() || undefined,
      }),
    );
  }, [
    dispatch,
    pageConfig,
    selectedBranchId,
    selectedStudentId,
    selectedTerm,
    selectedType,
    selectedPaymentMethod,
    selectedVoteHeadId,
    debouncedSearch,
  ]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Modal Actions
  const handleGenerateFees = (): void => {
    if (selectedTerm && userInfo?.branchId) {
      dispatch(
        generateTermFees({
          termId: selectedTerm,
          branchId: userInfo.branchId,
        }),
      );
    }
  };

  const handleUpload = (): void => {
    if (uploadFile && selectedTerm) {
      dispatch(uploadBulkPayments({ file: uploadFile, termId: selectedTerm }));
    }
  };

  useEffect(() => {
    if (serverStatus?.status === 200) {
      setIsGenerateOpen(false);
      setIsReminderModalOpen(false);
      setIsUploadOpen(false);
      setUploadFile(null);
      dispatch(resetFinanceStateAction());
      fetchEntries();
    }
  }, [serverStatus, dispatch, fetchEntries]);

  const activeClassroom = classrooms?.find((c) => c.id === selectedClassroom);

  const columns: TableColumn<StudentFeeLedgerResponseDto>[] = useMemo(
    () => [
      {
        id: "studentName",
        label: "Student Name",
        minWidth: 160,
        format: (_, row) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.studentName}
          </Typography>
        ),
      },
      {
        id: "studentAdmissionNumber",
        label: "Admission #",
        minWidth: 120,
        format: (_, row) => (
          <b>{(row as any).admissionNumber ?? row.studentAdmissionNumber}</b>
        ),
      },
      {
        id: "transactionType",
        label: "Type",
        minWidth: 120,
        format: (_, row) => <StatusChip status={row.transactionType as any} />,
      },
      {
        id: "amount",
        label: "Amount (KES)",
        minWidth: 120,
        align: "right",
        format: (_, row) => (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color:
                row.transactionType === TransactionType.PAYMENT
                  ? "success.main"
                  : "text.primary",
            }}
          >
            {formatPlainNumber(row.amount)}
          </Typography>
        ),
      },
      {
        id: "narrative",
        label: "Narrative",
        minWidth: 180,
        format: (_, row) => row.narrative || "-",
      },
      {
        id: "createdAt",
        label: "Date",
        minWidth: 120,
        format: (_, row) =>
          row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "-",
      },
    ],
    [],
  );

  const renderRowActions = (row: StudentFeeLedgerResponseDto) => (
    <Tooltip title="View Fee Statement">
      <IconButton
        size="small"
        color="primary"
        onClick={() =>
          navigate(
            `/student-fee-ledgers/${row.studentId}?termId=${selectedTerm || row.termId}`,
          )
        }
      >
        <VisibilityOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <PageContainer
      title="Student Fee Ledgers"
      subtitle="Manage global student billing records, transactions, and statements."
      actions={
        <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap", gap: 1 }}>
          {canCreate && (
            <AppButton
              variant="outlined"
              color="secondary"
              startIcon={<UploadFileIcon />}
              onClick={() => setIsUploadOpen(true)}
            >
              Import Payments
            </AppButton>
          )}
          {canCreate && (
            <AppButton
              variant="contained"
              color="secondary"
              startIcon={<AutoModeIcon />}
              onClick={() => setIsGenerateOpen(true)}
              disabled={!selectedTerm}
            >
              Generate Fees
            </AppButton>
          )}
          {canCreate && (
            <AppButton
              variant="contained"
              color="warning"
              startIcon={<NotificationsActiveIcon />}
              onClick={() => setIsReminderModalOpen(true)}
              disabled={!selectedClassroom}
            >
              Send Reminders
            </AppButton>
          )}
        </Stack>
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setPageConfig((p) => ({ ...p, page: 1 }));
        }}
        searchPlaceholder="Search by student or admission #..."
      >
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={(bId) => {
            setSelectedBranchId(bId);
            setPageConfig((p) => ({ ...p, page: 1 }));
          }}
        />

        <StudentSearchSelect
          value={selectedStudentId}
          branchId={selectedBranchId}
          onChange={setSelectedStudentId}
        />
        <TermSelectFilter value={selectedTerm} onChange={setSelectedTerm} />
        <AppSelectFilter
          label="Type"
          value={selectedType}
          onChange={setSelectedType}
          options={TRANSACTION_TYPE_OPTIONS}
          allLabel="All Types"
        />
        <AppSelectFilter
          label="Method"
          value={selectedPaymentMethod}
          onChange={setSelectedPaymentMethod}
          options={PAYMENT_METHOD_OPTIONS}
          allLabel="All Methods"
        />
        <VoteHeadSelectFilter
          value={selectedVoteHeadId}
          onChange={setSelectedVoteHeadId}
        />
        <ClassroomSelectFilter
          label="Class for Reminders"
          value={selectedClassroom}
          onChange={setSelectedClassroom}
          allLabel="-- Select Class --"
        />
      </PageToolbar>

      <StandardTable<StudentFeeLedgerResponseDto>
        columns={columns}
        data={ledgerEntries}
        loading={loadingLedgerEntries}
        error={errorLoadingLedgerEntries}
        onRetry={fetchEntries}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(p) => setPageConfig((prev) => ({ ...prev, page: p }))}
        onRowsPerPageChange={(l) =>
          setPageConfig((prev) => ({ ...prev, limit: l, page: 1 }))
        }
        emptyMessage="No ledger entries found"
        rowAction={renderRowActions}
      />

      {/* GENERATE MODAL */}
      <ConfirmDialog
        open={isGenerateOpen}
        onClose={() => setIsGenerateOpen(false)}
        onConfirm={handleGenerateFees}
        title="Generate Term Fees"
        description="This will create debit invoices for all active students based on standard Fee Allocations for the selected term. Existing entries are skipped. Proceed?"
        loading={loadingBatchGeneration}
      />

      {/* REMINDER MODAL */}
      {isReminderModalOpen && (
        <FeeReminderModal
          open={isReminderModalOpen}
          onClose={() => setIsReminderModalOpen(false)}
          classroomId={selectedClassroom}
          classroomName={activeClassroom?.name}
          loading={loadingReminders}
          error={errorReminders}
        />
      )}

      {/* UPLOAD MODAL */}
      <AppModal
        open={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        title="Import Payments Excel (.xlsx)"
        loading={loadingUpload}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Upload an Excel document (.xlsx) with recorded bank deposits. The
            ledger will parse rows and credit student balances immediately.
          </Typography>
          <AppButton variant="outlined">
            Choose Spreadsheet
            <input
              type="file"
              hidden
              accept=".xlsx"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setUploadFile(e.target.files[0]);
                }
              }}
            />
          </AppButton>
          {uploadFile && (
            <Typography
              variant="caption"
              sx={{ color: "primary.main", fontWeight: 700 }}
            >
              Selected: {uploadFile.name}
            </Typography>
          )}
          {loadingUpload && <AppLinearProgress label="Processing rows..." />}
          <AppButton
            onClick={handleUpload}
            disabled={!uploadFile || loadingUpload}
          >
            Upload & Post
          </AppButton>
        </Stack>
      </AppModal>
    </PageContainer>
  );
};

export default StudentFeeLedgersPage;
